// ===========================================
// Reschedule Class Dialog (View Lesson page)
// ===========================================
// Form-driven reschedule for a recurring class, opened from LessonDetailPage.
// Mirrors the 1:1 clinic reschedule UX: a WeekTilePicker (term weeks) + a
// multi-day SlotPicker fed by the teacher's availability, so an admin can move a
// class to a different day AND/OR time. Supports two scopes via a toggle:
//   - "Just this class"  → single-occurrence reschedule (LessonException)
//   - "All classes"      → recurring day/time change from the selected week onward
// Conflicts are previewed live with an override switch (matching the calendar
// reschedule). If a replacement teacher covers an affected week, the admin must
// reinstate the regular teacher first (one click clears all affected weeks) so we
// never strand coverage on a slot that no longer exists.

import { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EventIcon from '@mui/icons-material/Event';
import RepeatIcon from '@mui/icons-material/Repeat';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HistoryIcon from '@mui/icons-material/History';
import { useQuery, useQueries } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';

import WeekTilePicker from '../../booking/WeekTilePicker';
import { useSessionWeeks } from './sessionWeeks';
import SlotPicker from '../../booking/SlotPicker';
import { TimeSlot } from '../../../api/hybridBooking.api';
import { Lesson, ConflictCheckResult } from '../../../api/lessons.api';
import { extraLessonApi, AvailableSlot } from '../../../api/booking.api';
import { extraLessonKeys } from '../../../hooks/useExtraLessonRequests';
import {
  parseUTCDate,
  currentWeekNumber,
  occurrenceDateForWeek,
  termWeekAnchor,
  formatUTCDate,
  startOfTodayUTC,
  termWeekCount,
  lessonWeekWindow,
  formatTermWeekDate,
} from '../../../utils/dateUtils';
import {
  useRescheduleLesson,
  useRescheduleSingleOccurrence,
  useCheckRescheduleConflicts,
  useCheckSingleOccurrenceConflicts,
  useRelatedLessons,
  useReplacementCoverage,
  useClearReplacementWeeks,
  useClearReplacementTeacher,
  useLessonExceptions,
} from '../../../hooks/useLessons';

const MAX_REASON_LENGTH = 500;

interface RescheduleClassDialogProps {
  open: boolean;
  lesson: Lesson;
  onClose: () => void;
  onSubmit?: () => void;
}

/**
 * Format a Date as a tz-stable YYYY-MM-DD key (UTC components).
 *
 * ⚠️ This string is passed to BOTH the query key and the API call, and
 * `adminGetAvailableSlots` passes a string through untouched — so the key
 * always names exactly the day queried, in any timezone. That is the invariant
 * that matters; it does NOT depend on matching `slotDateKey`, which uses LOCAL
 * getters to mirror the API's *Date* branch and so disagrees with this west of
 * UTC. If this site is ever changed to hand a Date to either the key or the
 * API, route it through `slotDateKey` instead of this helper, or the key and
 * the query will name different days.
 */
function toDateKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate()
  ).padStart(2, '0')}`;
}

export function RescheduleClassDialog({
  open,
  lesson,
  onClose,
  onSubmit,
}: RescheduleClassDialogProps) {
  const lessonId = lesson.id;
  const teacherId = lesson.teacherId;
  const termStartDate = lesson.Term?.startDate;
  const termEndDate = lesson.Term?.endDate;
  const duration = lesson.durationMins ?? 30;

  // ----- Local state -----
  const [mode, setMode] = useState<'single' | 'all'>('single');
  // SOURCE week: which occurrence is being moved (single mode). An admin in Wk 5
  // may reschedule Wk 8's class. In all-mode this is the "apply from" week.
  const [sourceWeek, setSourceWeek] = useState<number | undefined>(undefined);
  // TARGET week: which week's availability slots to show (single mode only).
  // Defaults to the source week but can differ (move Wk 8's class into Wk 9).
  const [targetWeek, setTargetWeek] = useState<number | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [applyToAllTerms, setApplyToAllTerms] = useState(false);
  const [notifyParents, setNotifyParents] = useState(true);
  // Whether the admin has explicitly touched the notify switch. Until they do,
  // the default follows the source week (off for a past occurrence — emailing
  // parents about a class that already happened is noise). Once touched, their
  // choice sticks and is never auto-changed underneath them.
  const [notifyTouched, setNotifyTouched] = useState(false);
  const [reason, setReason] = useState('');
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictCheckResult | null>(null);

  // The week whose slots we display + reschedule into:
  //  - single → the chosen target week
  //  - all    → the source ("apply from") week IS the target time source
  const slotWeek = mode === 'single' ? targetWeek : sourceWeek;

  // ----- Derived term-week info -----
  const lastWeek = useMemo(() => {
    if (!termStartDate || !termEndDate) return 0;
    return currentWeekNumber(termStartDate, parseUTCDate(termEndDate));
  }, [termStartDate, termEndDate]);

  const currentWeek = useMemo(() => {
    if (!termStartDate) return 0;
    return currentWeekNumber(termStartDate);
  }, [termStartDate]);

  // The weeks this CLASS runs (mid-term start / last week). Applies to the SOURCE
  // picker and the "from week onward" range only: the destination picker stays
  // unbounded because a one-off move may land outside the window (the occurrence
  // still belongs to its ORIGINAL week — plan R11).
  // A plain class's own weeks (a fortnightly class, a band's rehearsals): its off weeks hold no
  // session to move, and the server refuses them (400 NOT_A_SESSION_WEEK, /qa Phase 4 M1). A
  // HYBRID keeps every tile, as before — the server does not judge its weeks here.
  const lessonSessionWeeks = useSessionWeeks(lesson);
  const plainSessionWeeks = lesson.LessonType?.type === 'HYBRID' ? undefined : (lessonSessionWeeks ?? undefined);

  const classWindow = useMemo(
    () =>
      termStartDate && termEndDate
        ? lessonWeekWindow(lesson, termWeekCount(termStartDate, termEndDate))
        : null,
    [lesson, termStartDate, termEndDate]
  );

  // No week can be chosen as the SOURCE: in "all classes" mode past weeks are locked,
  // so a class whose last week has gone leaves every tile disabled. Say so instead of
  // showing a dead picker (single mode allows past weeks, so it only trips on an
  // empty/inverted window).
  const noSourceWeeks = useMemo(() => {
    if (!classWindow || !termStartDate || lesson.dayOfWeek == null) return false;
    const today = startOfTodayUTC();
    for (let w = classWindow.first; w <= classWindow.last; w++) {
      if (mode === 'single') return false;
      if (occurrenceDateForWeek(parseUTCDate(termStartDate), w, lesson.dayOfWeek) >= today) return false;
    }
    return true;
  }, [classWindow, termStartDate, lesson.dayOfWeek, mode]);

  // Is the occurrence being moved already in the past? Drives the "editing
  // history" warning. Mirrors WeekTilePicker's rule exactly: compare the actual
  // lesson-occurrence date in the Monday-anchored week against UTC midnight
  // today (UTC to match the UTC-parsed term dates — no local-tz off-by-one).
  const sourceWeekIsPast = useMemo(() => {
    if (!sourceWeek || !termStartDate || lesson.dayOfWeek == null) return false;
    const today = startOfTodayUTC();
    const occurrence = occurrenceDateForWeek(
      parseUTCDate(termStartDate),
      sourceWeek,
      lesson.dayOfWeek
    );
    return occurrence < today;
  }, [sourceWeek, termStartDate, lesson.dayOfWeek]);

  // Is the chosen DESTINATION in the past? Drives the "this records history"
  // notice under the target picker. Keyed on the selected SLOT's own date, not
  // the target week: a part-past week offers both elapsed and remaining days, so
  // the week is too coarse a signal and would warn on a forward-looking move.
  // `dateKey` is a UTC-midnight calendar day, compared against `startOfTodayUTC`
  // to match — the same rule as `sourceWeekIsPast`, no local-tz off-by-one.
  const targetSlotIsPast = useMemo(() => {
    if (mode !== 'single' || !selectedSlot) return false;
    return parseUTCDate(selectedSlot.dateKey) < startOfTodayUTC();
  }, [mode, selectedSlot]);

  // The date of the occurrence being moved — shown in the past-week warning so
  // the admin can confirm they picked the right one. `occurrenceDateForWeek`
  // returns a UTC-midnight date-ONLY value, so it MUST be formatted in UTC
  // (formatUTCDate) — date-fns `format` reads local time and would shift the
  // day (and weekday name) backwards for any viewer west of UTC.
  const sourceOccurrenceLabel = useMemo(() => {
    if (!sourceWeek || !termStartDate || lesson.dayOfWeek == null) return '';
    const occurrence = occurrenceDateForWeek(
      parseUTCDate(termStartDate),
      sourceWeek,
      lesson.dayOfWeek
    );
    return formatUTCDate(occurrence, { weekday: 'long', day: 'numeric', month: 'long' });
  }, [sourceWeek, termStartDate, lesson.dayOfWeek]);

  // A "Teacher unavailable" conflict is NOT a booking clash — it means the target
  // slot falls outside the teacher's recurring WEEKLY availability windows. That
  // reads as a mystery when moving a class to a catch-up slot, a common shape for
  // this feature, so spell out the cause.
  //
  // The backend (`isTeacherAvailableAt`) returns TWO distinct reasons and the hint
  // MUST match which one came back — asserting the day-branch meaning on a
  // time-branch reason produces self-contradictory copy ("not available at this
  // time — Saturdays are outside their working hours"). Match on the reason text
  // and stay silent on anything unrecognised rather than guess.
  const scheduleConflictHint = useMemo(() => {
    const reason = conflicts?.scheduleConflict?.reason;
    if (!reason || !selectedSlot) return '';
    if (/on this day/i.test(reason)) {
      const weekday = formatUTCDate(parseUTCDate(selectedSlot.dateKey), { weekday: 'long' });
      return `— they don’t normally work ${weekday}s. Not a clash with another class.`;
    }
    if (/at this time/i.test(reason)) {
      return '— the slot falls outside their usual hours that day. Not a clash with another class.';
    }
    return '';
  }, [conflicts?.scheduleConflict?.reason, selectedSlot]);

  // Weeks that will be affected by this reschedule (used for the replacement check):
  //  - single  → just the source week (the occurrence being moved)
  //  - all     → source week through the end of term (the "from week onward" range)
  const affectedWeeks = useMemo<number[]>(() => {
    if (!sourceWeek) return [];
    if (mode === 'single') return [sourceWeek];
    const weeks: number[] = [];
    // Bounded by the class's last week: "onward" never reaches weeks it does not run.
    const through = classWindow ? Math.min(lastWeek, classWindow.last) : lastWeek;
    for (let w = sourceWeek; w <= through; w++) weeks.push(w);
    return weeks;
  }, [mode, sourceWeek, lastWeek, classWindow]);

  // ----- Hooks -----
  const checkRecurringConflicts = useCheckRescheduleConflicts();
  const checkSingleConflicts = useCheckSingleOccurrenceConflicts();
  const rescheduleLesson = useRescheduleLesson();
  const rescheduleSingle = useRescheduleSingleOccurrence();
  const { data: relatedLessons } = useRelatedLessons(open ? lessonId : null);
  const { data: coverage } = useReplacementCoverage(open ? lessonId : undefined);
  // Occurrences already moved/cancelled. Without these the dialog would show every
  // week at the lesson's RECURRING day/time, so an occurrence rescheduled earlier
  // still appears on its original week — see `movedOccurrences` below.
  const { data: lessonExceptions } = useLessonExceptions(open ? lessonId : undefined);
  const { mutate: clearWeeks, isPending: isClearingWeeks } = useClearReplacementWeeks();
  const { mutate: clearAll, isPending: isClearingAll } = useClearReplacementTeacher();

  // Teacher's available weekdays (0-6) — drives which days we fetch slots for.
  const { data: availableDays = [] } = useQuery({
    queryKey: extraLessonKeys.adminAvailabilityDays(teacherId),
    queryFn: () => extraLessonApi.adminGetTeacherAvailabilityDays(teacherId),
    enabled: open && !!teacherId,
  });

  // Concrete dates within the slot (target) week on which the teacher is available.
  // Weeks are Monday-anchored (termWeekAnchor): week N runs Mon..Sun, clamped
  // to the term's own start/end dates — matching WeekTilePicker's tiles.
  const weekDates = useMemo<Date[]>(() => {
    if (!slotWeek || !termStartDate || !termEndDate || availableDays.length === 0) {
      return [];
    }
    const termStart = parseUTCDate(termStartDate);
    const termEnd = parseUTCDate(termEndDate);
    const blockStart = addDays(termWeekAnchor(termStart), (slotWeek - 1) * 7);
    // A destination week may be PART past (the target picker's week-end rule
    // keeps it selectable while any day remains), so elapsed days are dropped
    // here. This is the ONLY past-date guard on this surface: the admin slots
    // endpoint applies no temporal filter at all and would happily return
    // a full grid for a date years ago.
    //
    // ⚠️ MUST stay in lockstep with the target picker's `allowPast`. In single
    // mode the picker unlocks past weeks (recording a class that already ran on
    // a different day — see the "recording history" alert below), so dropping
    // elapsed days here too would hand back a selectable week with ZERO
    // available days: a dead end that looks like a loading bug. In "all" mode
    // the destination is genuinely forward-looking, so the guard stays.
    // (2026-09-19, Andrew: staging walkthrough — a past source week was
    // selectable but no past destination was.)
    const allowPastTarget = mode === 'single';
    const today = startOfTodayUTC();
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(blockStart, i);
      if (d < termStart) continue; // week 1 of a mid-week term starts short
      if (d > termEnd) break;
      if (!allowPastTarget && d < today) continue; // already elapsed — not a valid destination
      if (availableDays.includes(d.getUTCDay())) dates.push(d);
    }
    return dates;
  }, [slotWeek, termStartDate, termEndDate, availableDays, mode]);

  // Fetch availability slots for each candidate day, then flatten into TimeSlot[].
  const slotQueries = useQueries({
    queries: weekDates.map((date) => {
      const dateKey = toDateKey(date);
      return {
        // Same endpoint as extraLessonKeys.adminAvailableSlots, so use that
        // factory member — a bare literal sits outside extraLessonKeys.all
        // and is missed by every slot-cache invalidation (md/review/invalidation.md).
        queryKey: extraLessonKeys.adminAvailableSlots(teacherId, dateKey, duration),
        queryFn: () => extraLessonApi.adminGetAvailableSlots(teacherId, dateKey, duration),
        enabled: open && !!teacherId && !!slotWeek,
      };
    }),
  });

  const slotsLoading = slotQueries.some((q) => q.isLoading);

  const slots = useMemo<TimeSlot[]>(() => {
    const out: TimeSlot[] = [];
    weekDates.forEach((date, idx) => {
      const dateKey = toDateKey(date);
      const daySlots = (slotQueries[idx]?.data ?? []) as AvailableSlot[];
      for (const s of daySlots) {
        out.push({
          date: dateKey,
          dateKey,
          startTime: s.startTime,
          endTime: s.endTime,
          weekNumber: slotWeek ?? 0,
          isAvailable: s.isAvailable,
        });
      }
    });
    return out;
    // slotQueries identity changes each render; key off the data via a stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDates, slotWeek, slotQueries.map((q) => q.dataUpdatedAt).join(',')]);

  // The actual occurrence date of the lesson in the SOURCE week (its existing
  // day/time). This is the `originalDate` for a single-occurrence reschedule.
  //
  // NOTE: this stays the RECURRING date even for an already-moved occurrence.
  // `LessonException.originalDate` is the exception's fixed identity key, so the
  // backend needs the recurring date to find/update the right row — sending the
  // moved-to date would create a SECOND exception. Where the occurrence currently
  // *sits* is a separate question, answered by `movedOccurrences` below.
  const sourceOccurrenceDateKey = useMemo<string | null>(() => {
    if (!sourceWeek || lesson.dayOfWeek == null || !termStartDate) return null;
    // Monday-anchored week grid — matches WeekTilePicker + backend getDateForWeek.
    return toDateKey(
      occurrenceDateForWeek(parseUTCDate(termStartDate), sourceWeek, lesson.dayOfWeek)
    );
  }, [sourceWeek, lesson.dayOfWeek, termStartDate]);

  // Where each already-rescheduled occurrence ACTUALLY sits now, keyed by the
  // recurring (original) date. Lets the dialog show "Wk 3 → moved to Fri 12 Sep"
  // instead of silently rendering it on its original week/day/time.
  const movedOccurrences = useMemo(() => {
    const byOriginalDateKey = new Map<
      string,
      { newDateKey: string; newStartTime: string | null; newEndTime: string | null }
    >();
    for (const exc of lessonExceptions ?? []) {
      if (exc.type !== 'RESCHEDULED' || !exc.newDate) continue;
      byOriginalDateKey.set(toDateKey(parseUTCDate(exc.originalDate)), {
        newDateKey: toDateKey(parseUTCDate(exc.newDate)),
        newStartTime: exc.newStartTime,
        newEndTime: exc.newEndTime,
      });
    }
    return byOriginalDateKey;
  }, [lessonExceptions]);

  // Cancelled occurrences, keyed by their recurring date. The backend refuses to
  // reschedule one ("This occurrence is cancelled. Restore it before
  // rescheduling." — 409) and there is NO restore UI anywhere in the app yet, so
  // submitting is a dead end. The calendar already blocks these (context menu +
  // drag); the dialog must too, or `allowPast` just makes them easier to reach.
  const cancelledOccurrenceKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const exc of lessonExceptions ?? []) {
      if (exc.type === 'CANCELLED') keys.add(toDateKey(parseUTCDate(exc.originalDate)));
    }
    return keys;
  }, [lessonExceptions]);

  const sourceWeekIsCancelled = sourceOccurrenceDateKey
    ? cancelledOccurrenceKeys.has(sourceOccurrenceDateKey)
    : false;


  // The exception covering the currently-selected source week, if any.
  const sourceWeekMove = sourceOccurrenceDateKey
    ? movedOccurrences.get(sourceOccurrenceDateKey)
    : undefined;

  // Whether the occurrence is ACTUALLY in the past right now. `sourceWeekIsPast`
  // judges the RECURRING date, which is wrong once an occurrence has been moved:
  // a past class already rescheduled to a future catch-up date is no longer past,
  // so "This class has already passed" would be a lie. Prefer the moved-to date
  // when there is one.
  const occurrenceIsActuallyPast = useMemo(() => {
    if (!sourceWeekMove) return sourceWeekIsPast;
    const today = startOfTodayUTC();
    return parseUTCDate(sourceWeekMove.newDateKey) < today;
  }, [sourceWeekMove, sourceWeekIsPast]);

  // The lesson's existing occurrence, surfaced as SlotPicker's `currentSlot` so it
  // renders highlighted — but only when the slot (target) week being shown is the
  // same as the source week, since that's the only week the occurrence appears in.
  const currentSlot = useMemo<TimeSlot | null>(() => {
    if (!sourceOccurrenceDateKey || !lesson.startTime) return null;
    // An already-moved occurrence lives at its exception's date/time, NOT the
    // recurring one — highlight where it actually is.
    const actualDateKey = sourceWeekMove?.newDateKey ?? sourceOccurrenceDateKey;
    const actualStart = sourceWeekMove?.newStartTime ?? lesson.startTime;
    const actualEnd = sourceWeekMove?.newEndTime ?? lesson.endTime ?? lesson.startTime;
    // Only highlight while the week being shown is the one containing it. For a
    // moved occurrence that is the week of its NEW date, which may differ from the
    // source week (e.g. Wk 3's class moved into Wk 5).
    const actualWeek = termStartDate
      ? currentWeekNumber(termStartDate, parseUTCDate(actualDateKey))
      : sourceWeek;
    if (slotWeek !== actualWeek) return null;
    return {
      date: actualDateKey,
      dateKey: actualDateKey,
      startTime: actualStart,
      endTime: actualEnd,
      weekNumber: actualWeek ?? 0,
      isAvailable: false,
    };
  }, [
    sourceOccurrenceDateKey,
    sourceWeekMove,
    slotWeek,
    sourceWeek,
    termStartDate,
    lesson.startTime,
    lesson.endTime,
  ]);

  // ----- Replacement-teacher awareness -----
  const replacementWeekCoverage = useMemo(
    () => (coverage?.weeks ?? []).filter((w) => affectedWeeks.includes(w.weekNumber)),
    [coverage, affectedWeeks]
  );
  // A legacy whole-term (global) replacement covers every week, so any affected
  // week is covered when `global` is set.
  const hasGlobalReplacement = !!coverage?.global && affectedWeeks.length > 0;
  const hasReplacementOnAffected = replacementWeekCoverage.length > 0 || hasGlobalReplacement;
  const reinstating = isClearingWeeks || isClearingAll;

  const replacementTeacherName = useMemo(() => {
    if (hasGlobalReplacement && coverage?.global) {
      const t = coverage.global.replacementTeacher;
      return `${t.firstName} ${t.lastName}`;
    }
    if (replacementWeekCoverage.length > 0) {
      const t = replacementWeekCoverage[0].replacementTeacher;
      return `${t.firstName} ${t.lastName}`;
    }
    return '';
  }, [hasGlobalReplacement, coverage, replacementWeekCoverage]);

  const handleReinstate = () => {
    if (hasGlobalReplacement) {
      // Whole-term clear covers every affected week in one shot.
      clearAll(lessonId);
      return;
    }
    const weeks = replacementWeekCoverage.map((w) => w.weekNumber);
    if (weeks.length > 0) clearWeeks({ lessonId, weekNumbers: weeks });
  };

  // ----- Conflict preview (re-runs when the chosen slot / mode changes) -----
  // Reset override whenever the target slot changes.
  useEffect(() => {
    setOverrideConflicts(false);
  }, [selectedSlot, mode]);

  // Default "notify parents" to OFF when moving an occurrence that has already
  // passed — parents don't need an email about a class that already happened
  // (they were typically told at the time). Only applies until the admin touches
  // the switch themselves, after which their choice is respected.
  //
  // Depends on `occurrenceIsActuallyPast`, NOT `sourceWeekIsPast`: the exceptions
  // query is async, so on first paint `sourceWeekMove` is undefined and
  // `occurrenceIsActuallyPast` falls back to the recurring date. Keying the dep on
  // the raw week left a stale closure — when the exceptions arrived and revealed
  // the occurrence had already been moved to a FUTURE date, nothing re-ran and the
  // switch stayed OFF, silently sending no email about a genuinely future class.
  useEffect(() => {
    if (notifyTouched) return;
    setNotifyParents(!(occurrenceIsActuallyPast && mode === 'single'));
  }, [occurrenceIsActuallyPast, mode, notifyTouched]);

  useEffect(() => {
    if (!selectedSlot) {
      setConflicts(null);
      return;
    }
    const newDate = parseUTCDate(selectedSlot.dateKey);
    const newDayOfWeek = newDate.getUTCDay();
    let cancelled = false;

    if (mode === 'all') {
      checkRecurringConflicts.mutate(
        {
          lessonId,
          input: {
            newDayOfWeek,
            newStartTime: selectedSlot.startTime,
            newEndTime: selectedSlot.endTime,
          },
        },
        { onSuccess: (res) => !cancelled && setConflicts(res) }
      );
    } else {
      const originalDate = sourceOccurrenceDateKey ?? selectedSlot.dateKey;
      checkSingleConflicts.mutate(
        {
          lessonId,
          input: {
            originalDate,
            newDate: selectedSlot.dateKey,
            newStartTime: selectedSlot.startTime,
            newEndTime: selectedSlot.endTime,
          },
        },
        { onSuccess: (res) => !cancelled && setConflicts(res) }
      );
    }
    return () => {
      cancelled = true;
    };
    // sourceOccurrenceDateKey is read for single-mode originalDate; include it so a
    // source-week change re-checks even if selectedSlot somehow survived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot, mode, lessonId, sourceOccurrenceDateKey]);

  // ----- Submit -----
  const resetAndClose = () => {
    setMode('single');
    setSourceWeek(undefined);
    setTargetWeek(undefined);
    setSelectedSlot(null);
    setApplyToAllTerms(false);
    setNotifyParents(true);
    // Clear the manual-override flag too, or the past-aware notify default
    // would never fire again for the rest of the page's life (this dialog stays
    // mounted, so state survives close/reopen).
    setNotifyTouched(false);
    setReason('');
    setOverrideConflicts(false);
    setConflicts(null);
    onClose();
    onSubmit?.();
  };

  const isRescheduling = rescheduleLesson.isPending || rescheduleSingle.isPending;

  const handleConfirm = () => {
    if (!selectedSlot || !sourceWeek) return;
    const newDate = parseUTCDate(selectedSlot.dateKey);
    const newDayOfWeek = newDate.getUTCDay();

    if (mode === 'all') {
      rescheduleLesson.mutate(
        {
          lessonId,
          input: {
            newDayOfWeek,
            newStartTime: selectedSlot.startTime,
            newEndTime: selectedSlot.endTime,
            fromWeekNumber: sourceWeek,
            applyToAllTerms,
            notifyParents,
            reason: reason || undefined,
            overrideConflicts,
          },
        },
        { onSuccess: resetAndClose }
      );
    } else {
      const originalDate = sourceOccurrenceDateKey ?? selectedSlot.dateKey;
      rescheduleSingle.mutate(
        {
          lessonId,
          input: {
            originalDate,
            newDate: selectedSlot.dateKey,
            newStartTime: selectedSlot.startTime,
            newEndTime: selectedSlot.endTime,
            notifyParents,
            reason: reason || undefined,
            overrideConflicts,
          },
        },
        { onSuccess: resetAndClose }
      );
    }
  };

  const handleClose = () => {
    if (!isRescheduling && !reinstating) resetAndClose();
  };

  const confirmDisabled =
    !selectedSlot ||
    // Single mode needs a resolved source occurrence date to key the exception;
    // without it we'd submit a bogus originalDate. Guard rather than fall back.
    (mode === 'single' && !sourceOccurrenceDateKey) ||
    // A cancelled occurrence can't be rescheduled — the backend 409s and there is
    // no restore UI, so submitting would be a guaranteed dead end.
    (mode === 'single' && sourceWeekIsCancelled) ||
    (conflicts?.hasConflicts && !overrideConflicts) ||
    hasReplacementOnAffected ||
    isRescheduling;

  const affectedWeeksLabel =
    affectedWeeks.length === 1
      ? `week ${affectedWeeks[0]}`
      : `weeks ${affectedWeeks[0]}–${affectedWeeks[affectedWeeks.length - 1]}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Reschedule Class
          {conflicts?.hasConflicts && <WarningIcon color="warning" />}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Mode toggle */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              What would you like to move?
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_e, val) => {
                if (val) {
                  setMode(val);
                  setSelectedSlot(null);
                  setTargetWeek(undefined);
                  setApplyToAllTerms(false);
                  // Past weeks are selectable in single mode only. Switching to
                  // "all" re-locks them, so a past sourceWeek would stay visibly
                  // selected while disabled — and would submit a past
                  // "apply from" week for a PERMANENT recurring change. Clear it.
                  if (val === 'all' && sourceWeekIsPast) setSourceWeek(undefined);
                }
              }}
              size="small"
              fullWidth
              aria-label="Reschedule scope"
            >
              <ToggleButton value="single">
                <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
                Just this class
              </ToggleButton>
              <ToggleButton value="all">
                <RepeatIcon sx={{ mr: 0.5 }} fontSize="small" />
                All classes
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {mode === 'single'
                ? 'Only the chosen week’s occurrence will be moved. It can move to a different day, time, or week. All other weeks stay unchanged.'
                : 'The class will permanently move to the new day/time from the chosen week onward.'}
            </Typography>
          </Box>

          <Divider />

          {/* Source-week picker: which class is being rescheduled */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {mode === 'single' ? 'Which week’s class to reschedule' : 'Apply from week'}
            </Typography>
            <WeekTilePicker
              termStartDate={termStartDate || ''}
              termEndDate={termEndDate || ''}
              selectedWeek={sourceWeek}
              onWeekSelect={(w) => {
                setSourceWeek(w);
                setSelectedSlot(null);
                // In single mode, default the target week to the source week
                // (admin can change it below); in all mode there is no separate
                // target — the source week IS the time source.
                setTargetWeek(w);
              }}
              currentWeek={currentWeek}
              occurrenceDayOfWeek={lesson.dayOfWeek ?? undefined}
              // Past weeks are selectable HERE ONLY (single mode) so an admin can
              // move a class that already passed — e.g. the teacher was away and
              // nobody rescheduled it at the time. The backend already allows this
              // inside a running term (the ended-term 409 still applies).
              // In "all classes" mode this is the "apply from" week for a
              // recurring change, where a past start makes no sense — so the
              // unlock is scoped to single mode.
              allowPast={mode === 'single'}
              // Single mode picks an existing SESSION: never an off week. "Apply from week N" is a
              // boundary, and an off week is a fine place to start the new schedule.
              sessionWeeks={mode === 'single' ? plainSessionWeeks : undefined}
              windowFirst={classWindow?.first}
              windowLast={classWindow?.last}
            />
            {noSourceWeeks && classWindow && (
              <Alert severity="info" sx={{ mt: 1 }} data-testid="no-remaining-weeks">
                {classWindow.first <= classWindow.last && termStartDate
                  ? `This class has no remaining weeks to reschedule — its last week was ${formatTermWeekDate(termStartDate, classWindow.last, lesson.dayOfWeek)}.`
                  : 'This class has no weeks to reschedule.'}
              </Alert>
            )}
          </Box>

          {/* Cancelled occurrence — reschedule would 409 with no way forward. */}
          {mode === 'single' && sourceWeekIsCancelled && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                This class was cancelled
              </Typography>
              <Typography variant="body2">
                A cancelled class can’t be rescheduled. Pick a different week, or
                restore this occurrence first.
              </Typography>
            </Alert>
          )}

          {/* Already-moved notice. The week tiles are built from the RECURRING
              schedule, so without this an occurrence rescheduled earlier looks
              untouched on its original week. */}
          {mode === 'single' && sourceWeekMove && (
            <Alert severity="info" icon={<EventIcon />}>
              <Typography variant="body2">
                This class has already been moved to{' '}
                <strong>
                  {formatUTCDate(parseUTCDate(sourceWeekMove.newDateKey), {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                  {sourceWeekMove.newStartTime ? ` at ${sourceWeekMove.newStartTime}` : ''}
                </strong>
                . Rescheduling again will move it from there.
              </Typography>
            </Alert>
          )}

          {/* Past-occurrence warning — editing history should feel deliberate. */}
          {occurrenceIsActuallyPast && mode === 'single' && (
            <Alert severity="warning" icon={<HistoryIcon />}>
              <Typography variant="subtitle2" gutterBottom>
                This class has already passed
              </Typography>
              <Typography variant="body2">
                You’re rescheduling{' '}
                {sourceWeekMove
                  ? formatUTCDate(parseUTCDate(sourceWeekMove.newDateKey), {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })
                  : sourceOccurrenceLabel}
                , which is in the past. This records the
                class at its new date — useful when a class didn’t run and wasn’t rescheduled at the
                time.
                {notifyParents ? ' Parents will be notified.' : ' Parents will not be notified.'}
              </Typography>
            </Alert>
          )}

          {/* Target-week picker (single mode only): which week to move it to */}
          {mode === 'single' && sourceWeek && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Move to week
              </Typography>
              {/* ⚠️ `allowPast` is scoped to SINGLE mode, and was deliberately
                  absent until 2026-09-19. The original rule was "a catch-up slot
                  is by definition still to come", which is true for a make-up but
                  NOT for the other thing this dialog does: recording that a class
                  actually ran on a different past day and was never written down
                  at the time. That is precisely what the retro-edit audit log
                  exists to capture, and the UI could not express it (Andrew,
                  staging walkthrough — a past SOURCE week was selectable while
                  every past DESTINATION was disabled, with no explanation).

                  The backend has never blocked this: `rescheduleSingleOccurrence`
                  validates only that `newDate` falls inside the term and that the
                  term has not ended. The block was purely client-side.

                  ⚠️ Unlocking the week is HALF the change — `weekDates` above
                  carries its own `d < today` guard and must stay in lockstep, or
                  the week becomes selectable with zero available days.

                  In "all" mode the destination really is forward-looking (an
                  "apply from" week for a recurring change), so it stays locked.

                  `pastRule="week-end"` because this is a DESTINATION week: the
                  class can move to a different DAY, so the week stays valid
                  until its last day elapses. Judging it by the lesson's own
                  weekday locked the whole week the moment that day passed,
                  hiding still-bookable later days (MNM: on Saturday, the next
                  day's slot was unreachable because the class runs midweek).
                  `weekDates` above drops the elapsed days, so a part-past week
                  offers only its remaining ones. */}
              <WeekTilePicker
                termStartDate={termStartDate || ''}
                termEndDate={termEndDate || ''}
                selectedWeek={targetWeek}
                onWeekSelect={(w) => {
                  setTargetWeek(w);
                  setSelectedSlot(null);
                }}
                currentWeek={currentWeek}
                occurrenceDayOfWeek={lesson.dayOfWeek ?? undefined}
                pastRule="week-end"
                allowPast={mode === 'single'}
              />

              {/* The audit consequence, stated where the choice is made. A past
                  destination is a historical correction, not a booking — say so
                  before submit rather than letting the row appear in the owner's
                  report unannounced. Gated on the SELECTED target actually being
                  past, so an ordinary forward move stays uncluttered. */}
              {targetSlotIsPast && (
                <Alert severity="info" icon={<HistoryIcon />} sx={{ mt: 1.5 }}>
                  <Typography variant="body2">
                    You’re moving this class to a date that has already passed. This records what
                    happened rather than scheduling something — it will appear in the{' '}
                    <strong>retroactive edit report</strong> against your name.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Replacement-teacher warning + reinstate */}
          {hasReplacementOnAffected && (
            <Alert
              severity="warning"
              icon={<PersonOffIcon />}
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  onClick={handleReinstate}
                  disabled={reinstating}
                >
                  {reinstating ? 'Reinstating…' : 'Reinstate regular teacher'}
                </Button>
              }
            >
              <Typography variant="subtitle2" gutterBottom>
                Replacement teacher on this class
              </Typography>
              <Typography variant="body2">
                {replacementTeacherName} is covering {affectedWeeksLabel} of this class. Reinstate
                the regular teacher before rescheduling so the replacement isn’t left on a time that
                no longer exists.
              </Typography>
            </Alert>
          )}

          {/* Slot picker */}
          {slotWeek && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Available times
              </Typography>
              <SlotPicker
                slots={slots}
                selectedSlot={selectedSlot}
                onSlotSelect={setSelectedSlot}
                isLoading={slotsLoading}
                currentSlot={currentSlot}
                emptyMessage="No available slots — check this teacher’s availability."
                confirmationPrefix="Rescheduling to"
              />
            </Box>
          )}

          {/* Conflicts */}
          {conflicts?.hasConflicts && (
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                Conflicts Detected
              </Typography>
              {conflicts.teacherConflict && (
                <Typography variant="body2">
                  Teacher conflict: {conflicts.teacherConflict.lessonName} on{' '}
                  {format(parseISO(conflicts.teacherConflict.date), 'EEE d MMM')} at{' '}
                  {conflicts.teacherConflict.time}
                </Typography>
              )}
              {conflicts.roomConflict && (
                <Typography variant="body2">
                  Room conflict: {conflicts.roomConflict.lessonName} on{' '}
                  {format(parseISO(conflicts.roomConflict.date), 'EEE d MMM')} at{' '}
                  {conflicts.roomConflict.time}
                </Typography>
              )}
              {conflicts.scheduleConflict && (
                <Typography variant="body2">
                  Teacher unavailable: {conflicts.scheduleConflict.reason}
                  {scheduleConflictHint && (
                    <>
                      {' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {scheduleConflictHint}
                      </Box>
                    </>
                  )}
                </Typography>
              )}
              <FormControlLabel
                sx={{ mt: 1, mb: 0, ml: 1 }}
                control={
                  <Switch
                    checked={overrideConflicts}
                    onChange={(e) => setOverrideConflicts(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="medium">
                    Override conflicts and reschedule anyway
                  </Typography>
                }
              />
            </Alert>
          )}

          {/* Affected students */}
          {conflicts && conflicts.affectedStudents > 0 && (
            <Alert severity="info">
              This will affect {conflicts.affectedStudents} enrolled{' '}
              {conflicts.affectedStudents === 1 ? 'student' : 'students'}.
            </Alert>
          )}

          {/* Apply to all terms — all-mode only, when related lessons exist */}
          {mode === 'all' && relatedLessons && relatedLessons.length > 0 && (
            <Alert severity="info">
              <FormControlLabel
                control={
                  <Switch
                    checked={applyToAllTerms}
                    onChange={(e) => setApplyToAllTerms(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Apply to all terms ({relatedLessons.length + 1} lessons)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Also reschedule: {relatedLessons.map((l) => l.termName).join(', ')}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={notifyParents}
                onChange={(e) => {
                  setNotifyParents(e.target.checked);
                  setNotifyTouched(true);
                }}
              />
            }
            label="Notify parents via email"
          />
          {occurrenceIsActuallyPast && mode === 'single' && !notifyParents && !notifyTouched && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Notifications are off by default for a class that has already passed.
            </Typography>
          )}

          <TextField
            label="Reason for rescheduling (optional)"
            multiline
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            placeholder="e.g., Teacher unavailable, Room maintenance"
            fullWidth
            helperText={`${reason.length}/${MAX_REASON_LENGTH} characters`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isRescheduling}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={confirmDisabled}>
          {isRescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RescheduleClassDialog;
